import * as d3 from "d3";

/**
 * Shared 4-point OTP derating graph renderer used by both
 * OtpFtpGraphComponent (resistance axis, right-to-left) and
 * OtpDtpGraphComponent (temperature axis, left-to-right).
 */

export interface OtpDeratingRenderOptions {
  /** DOM id (no `#`) of the target `<svg>`. */
  readonly svgId: string;
  /** Unique gradient id used for the fill. */
  readonly gradientId: string;
  /** X-axis label (may contain HTML entities). */
  readonly xAxisLabel: string;
  /** Whether xAxisLabel should be rendered as HTML (true) or text (false). */
  readonly xAxisLabelAsHtml?: boolean;
  /** Chart height (before margins). */
  readonly chartHeight: number;
  /** X domain (may be reversed for right-to-left axes). */
  readonly xDomain: [number, number];
  /** Y domain. */
  readonly yDomain: [number, number];
  /** Tick count for the x-axis (both axis and grid). */
  readonly xTicks: number;
}

export const OTP_GRAPH_MARGIN = { top: 20, right: 30, bottom: 45, left: 55 };
export const OTP_GRAPH_WIDTH_PX = 760;
export const OTP_Y_AXIS_LABEL = "Dim level (%)";

/**
 * Render a 4-point derating curve into the SVG identified by `opts.svgId`.
 * All previous contents of the SVG are cleared first. If the SVG is not
 * in the DOM, this is a no-op.
 */
export function renderOtpDeratingGraph(data: any[], opts: OtpDeratingRenderOptions): void {
  const svg = d3.select(`#${opts.svgId}`);
  if (svg.empty()) {
    return;
  }

  const margin = OTP_GRAPH_MARGIN;
  const width = OTP_GRAPH_WIDTH_PX - margin.left - margin.right;
  const height = opts.chartHeight - margin.top - margin.bottom;

  d3.selectAll(`#${opts.svgId} > *`).remove();

  const x = d3.scaleLinear().range([0, width]);
  const y = d3.scaleLinear().range([height, 0]);

  x.domain(opts.xDomain);
  y.domain(opts.yDomain);

  const area = d3
    .area<any>()
    .x((d: any) => x(d.x))
    .y0(height)
    .y1((d: any) => y(d.y));

  const line = d3
    .line<any>()
    .x((d: any) => x(d.x))
    .y((d: any) => y(d.y));

  const focus = svg
    .append("g")
    .attr("transform", `translate(${margin.left},${margin.top})`);

  const maxY = d3.max(data, (d: any) => d.y);

  svg
    .append("linearGradient")
    .attr("id", opts.gradientId)
    .attr("gradientUnits", "userSpaceOnUse")
    .attr("x1", 0)
    .attr("y1", y(0))
    .attr("x2", 0)
    .attr("y2", y(maxY))
    .selectAll("stop")
    .data([
      { offset: "10%", color: "transparent" },
      { offset: "30%", color: "#00E487" },
      { offset: "100%", color: "#00b36b" },
    ])
    .enter()
    .append("stop")
    .attr("offset", (d: any) => d.offset)
    .attr("stop-color", (d: any) => d.color);

  focus
    .append("path")
    .datum(data)
    .attr("fill", `url('#${opts.gradientId}')`)
    .attr("fill-opacity", 0.35)
    .attr("stroke", "#1ECBD0")
    .attr("stroke-width", "1px")
    .attr("d", area);

  focus
    .append("path")
    .datum(data)
    .attr("fill", "none")
    .attr("stroke", "#1ECBD0")
    .attr("stroke-width", "2px")
    .attr("d", line);

  focus
    .selectAll("circle")
    .data(data)
    .enter()
    .append("circle")
    .attr("r", 4)
    .attr("cx", (d: any) => x(d.x))
    .attr("cy", (d: any) => y(d.y))
    .style("fill", "#1ECBD0");

  focus
    .append("g")
    .attr("class", "axis axis--x")
    .attr("transform", `translate(0,${height})`)
    .call(d3.axisBottom(x).ticks(opts.xTicks));

  focus
    .append("g")
    .attr("class", "axis axis--y")
    .call(d3.axisLeft(y).ticks(10));

  focus
    .append("g")
    .attr("class", "grid")
    .attr("transform", `translate(0,${height})`)
    .call(d3.axisBottom(x).ticks(opts.xTicks).tickSize(-height).tickFormat("" as any));

  focus
    .append("g")
    .attr("class", "grid")
    .call(d3.axisLeft(y).ticks(10).tickSize(-width).tickFormat("" as any));

  const xLabel = svg
    .append("text")
    .attr("x", margin.left + width / 2)
    .attr("y", margin.top + height + 38)
    .style("text-anchor", "middle")
    .style("font", "16px Signify-Relative-Bold");

  if (opts.xAxisLabelAsHtml) {
    xLabel.html(opts.xAxisLabel);
  } else {
    xLabel.text(opts.xAxisLabel);
  }

  svg
    .append("text")
    .attr("transform", "rotate(-90)")
    .attr("y", 16)
    .attr("x", -(margin.top + height / 2))
    .style("text-anchor", "middle")
    .style("font", "16px Signify-Relative-Bold")
    .text(OTP_Y_AXIS_LABEL);
}
